<template>
  <q-page class="guia-page">
    <div class="guia-container">
      <!-- Header -->
      <div class="guia-header">
        <div>
          <h1 class="guia-title">Guia Operativa</h1>
          <p class="guia-subtitle">
            {{ viewMode === 'learning' ? 'Aprende cada protocolo en profundidad' : 'Consulta rapida en tu sede' }}
          </p>
        </div>
        <div class="guia-header-right">
          <div class="guia-mode-toggle">
            <button
              v-for="m in (['learning', 'realtime'] as const)"
              :key="m"
              class="mode-btn"
              :class="{ 'mode-btn-active': viewMode === m }"
              @click="toggleMode(m)"
            >
              {{ m === 'learning' ? 'Aprendizaje' : 'Tiempo Real' }}
            </button>
          </div>
          <span class="guia-count">{{ situations?.length ?? 0 }}</span>
        </div>
      </div>

      <!-- Search bar -->
      <q-input
        v-model="search"
        outlined
        dark
        dense
        placeholder="Buscar situacion..."
        debounce="300"
        class="guia-search"
      >
        <template #prepend>
          <q-icon name="search" size="16px" style="color: var(--oa-text-disabled);" />
        </template>
        <template v-if="search" #append>
          <q-icon name="close" size="14px" style="color: var(--oa-text-disabled); cursor: pointer;" @click="search = ''" />
        </template>
      </q-input>

      <!-- Filters row -->
      <div class="guia-filters">
        <button
          class="filter-pill"
          :class="{ 'filter-pill-active': !selectedCategory }"
          @click="selectedCategory = ''"
        >
          Todas
        </button>
        <template v-for="(label, key) in CATEGORY_LABELS" :key="key">
          <button
            v-if="(categoryCounts[key] ?? 0) > 0"
            class="filter-pill"
            :class="{ 'filter-pill-active': key === selectedCategory }"
            @click="selectedCategory = key === selectedCategory ? '' : key"
          >
            {{ label }} {{ categoryCounts[key] ?? 0 }}
          </button>
        </template>
        <span class="filter-separator" />
        <button
          class="filter-pill"
          :class="{ 'filter-pill-active': !selectedRole }"
          @click="selectedRole = ''"
        >
          Todos
        </button>
        <button
          v-for="r in ROLE_OPTIONS"
          :key="r.key"
          class="filter-pill"
          :class="{ 'filter-pill-active': r.key === selectedRole }"
          @click="selectedRole = r.key === selectedRole ? '' : r.key"
        >
          {{ r.label }}
        </button>
      </div>

      <!-- Loading -->
      <template v-if="loading">
        <q-skeleton v-for="n in 4" :key="n" type="rect" height="64px" dark style="border-radius: 12px; margin-bottom: 8px;" />
      </template>

      <!-- Empty -->
      <div v-else-if="!situations?.length" class="guia-empty">
        <p class="guia-empty-text">Sin resultados</p>
      </div>

      <!-- Situation list grouped by category -->
      <div v-else class="guia-groups">
        <div v-for="[category, items] in groupedSituations" :key="category" class="guia-group">
          <!-- Category label -->
          <div class="guia-group-label">
            <div class="guia-group-dot" :style="{ background: CATEGORY_COLORS[category] ?? 'var(--color-charcoal-mist)' }" />
            <span class="guia-group-name">{{ CATEGORY_LABELS[category] ?? category }}</span>
            <span class="guia-group-count">{{ items.length }}</span>
          </div>

          <!-- Situations -->
          <div class="guia-group-list">
            <router-link
              v-for="(sit, i) in items"
              :key="sit.id"
              :to="{ name: 'guia-situation', params: { situationId: sit.id } }"
              class="guia-sit-row"
              :style="{ borderBottom: i < items.length - 1 ? '1px solid var(--oa-border)' : undefined }"
            >
              <div class="guia-sit-diff-bar" :style="{ background: DIFFICULTY_COLORS[sit.difficulty] ?? 'var(--color-charcoal-mist)', opacity: 0.6 }" />
              <div class="guia-sit-content">
                <div class="guia-sit-top">
                  <span class="guia-sit-code">{{ sit.code }}</span>
                  <span class="guia-sit-title">{{ sit.title }}</span>
                </div>
                <p v-if="sit.triggerQuestion" class="guia-sit-trigger">{{ sit.triggerQuestion }}</p>
              </div>
              <span class="guia-sit-diff-label" :style="{ color: DIFFICULTY_COLORS[sit.difficulty] ?? 'var(--color-charcoal-mist)', opacity: 0.7 }">
                {{ sit.difficulty }}
              </span>
              <span class="guia-sit-steps">{{ sit.totalSteps }}p</span>
              <q-icon name="chevron_right" size="14px" style="color: var(--oa-text-disabled); flex-shrink: 0;" />
            </router-link>
          </div>
        </div>
      </div>
    </div>
  </q-page>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue';
import { api } from 'boot/axios';

// ---------- Types ----------
interface Situation {
  id: string;
  code: string;
  title: string;
  subtitle?: string;
  triggerQuestion?: string;
  category: string;
  difficulty: string;
  flowPattern?: string;
  totalSteps: number;
  keywords?: string[];
}

// ---------- Constants ----------
const CATEGORY_COLORS: Record<string, string> = {
  recepcion: '#60a5fa', alumnos: '#a78bfa', clase: '#4ade80',
  experiencia: '#f472b6', dificiles: '#f87171', operaciones: '#fbbf24',
  evaluacion: '#22d3ee', formacion: '#818cf8', equipo: '#2dd4bf', ventas: '#fb923c',
};

const CATEGORY_LABELS: Record<string, string> = {
  recepcion: 'Recepcion', alumnos: 'Alumnos', clase: 'Clase',
  experiencia: 'Experiencia', dificiles: 'Situaciones Dificiles', operaciones: 'Operaciones',
  evaluacion: 'Evaluacion', formacion: 'Formacion', equipo: 'Equipo', ventas: 'Ventas',
};

const DIFFICULTY_COLORS: Record<string, string> = {
  basico: '#4ade80', intermedio: '#fbbf24', avanzado: '#f87171',
};

const ROLE_OPTIONS = [
  { key: 'cc', label: 'CC' },
  { key: 'sc', label: 'SC' },
  { key: 'ot_1', label: 'OT 1' },
  { key: 'ot_2', label: 'OT 2' },
  { key: 'ot_3', label: 'OT 3' },
];

// ---------- State ----------
const loading = ref(true);
const search = ref('');
const selectedCategory = ref('');
const selectedRole = ref('');
const viewMode = ref<'learning' | 'realtime'>('learning');
const situations = ref<Situation[]>([]);
const categoryCounts = ref<Record<string, number>>({});

// ---------- Computed ----------
const groupedSituations = computed(() => {
  const grouped = new Map<string, Situation[]>();
  if (situations.value) {
    for (const sit of situations.value) {
      if (!grouped.has(sit.category)) grouped.set(sit.category, []);
      grouped.get(sit.category)!.push(sit);
    }
  }
  return [...grouped.entries()];
});

// ---------- Mode toggle ----------
function toggleMode(mode: 'learning' | 'realtime') {
  viewMode.value = mode;
  try { localStorage.setItem('guide-view-mode', mode); } catch { /* noop */ }
}

// ---------- Data Fetching ----------
async function fetchSituations() {
  loading.value = true;
  try {
    const params: Record<string, string> = {};
    if (selectedCategory.value) params.category = selectedCategory.value;
    if (search.value) params.search = search.value;
    if (selectedRole.value) params.role = selectedRole.value;
    const { data } = await api.get('/guide', { params });
    situations.value = data;
  } catch {
    situations.value = [];
  } finally {
    loading.value = false;
  }
}

async function fetchCategories() {
  try {
    const { data } = await api.get('/guide/categories');
    categoryCounts.value = data;
  } catch {
    categoryCounts.value = {};
  }
}

// Watch filter changes
watch([search, selectedCategory, selectedRole], () => {
  fetchSituations();
});

// ---------- Lifecycle ----------
onMounted(() => {
  // Restore view mode
  try {
    const stored = localStorage.getItem('guide-view-mode');
    if (stored === 'learning' || stored === 'realtime') viewMode.value = stored;
  } catch { /* noop */ }

  fetchSituations();
  fetchCategories();
});
</script>

<style scoped>
.guia-page {
  padding: 24px;
}

.guia-container {
  max-width: 760px;
  margin: 0 auto;
}

/* Header */
.guia-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 16px;
}

.guia-title {
  font-size: 20px;
  font-weight: 600;
  color: var(--oa-text-primary);
  margin: 0;
}

.guia-subtitle {
  font-size: 12px;
  color: var(--oa-text-muted);
  margin: 4px 0 0;
}

.guia-header-right {
  display: flex;
  align-items: center;
  gap: 8px;
}

.guia-mode-toggle {
  display: flex;
  border-radius: 9999px;
  padding: 2px;
  background: var(--oa-bg-3);
}

.mode-btn {
  padding: 4px 10px;
  border-radius: 9999px;
  font-size: 11px;
  font-weight: 500;
  border: none;
  cursor: pointer;
  transition: all 0.2s;
  background: transparent;
  color: var(--oa-text-muted);
  font-family: inherit;
}

.mode-btn-active {
  background: var(--oa-gold-full);
  color: var(--color-marble-cream);
}

.guia-count {
  font-size: 11px;
  font-family: monospace;
  color: var(--oa-text-disabled);
}

/* Search */
.guia-search {
  margin-bottom: 12px;
}

/* Filters */
.guia-filters {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  align-items: center;
  margin-bottom: 16px;
}

.filter-pill {
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 500;
  border: none;
  cursor: pointer;
  transition: all 0.2s;
  background: transparent;
  color: var(--oa-text-muted);
  font-family: inherit;
}

.filter-pill-active {
  background: var(--oa-gold-full);
  color: var(--color-marble-cream);
}

.filter-separator {
  width: 1px;
  height: 16px;
  background: var(--oa-border);
  margin: 0 4px;
}

/* Empty */
.guia-empty {
  padding: 40px;
  text-align: center;
  background: var(--oa-bg-2);
  border-radius: 8px;
}

.guia-empty-text {
  font-size: 12px;
  color: var(--oa-text-muted);
  margin: 0;
}

/* Groups */
.guia-groups {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.guia-group-label {
  display: flex;
  align-items: center;
  gap: 8px;
  padding-left: 4px;
  margin-bottom: 6px;
}

.guia-group-dot {
  width: 6px;
  height: 6px;
  border-radius: 9999px;
}

.guia-group-name {
  font-size: 12px;
  font-weight: 600;
  color: var(--oa-text-primary);
}

.guia-group-count {
  font-size: 10px;
  color: var(--oa-text-disabled);
}

/* Situation list */
.guia-group-list {
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid var(--oa-border);
}

.guia-sit-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 14px;
  background: var(--oa-bg-2);
  text-decoration: none;
  transition: background 0.15s;
}

.guia-sit-row:hover {
  background: var(--oa-bg-3);
}

.guia-sit-diff-bar {
  width: 4px;
  height: 24px;
  border-radius: 9999px;
  flex-shrink: 0;
}

.guia-sit-content {
  flex: 1;
  min-width: 0;
}

.guia-sit-top {
  display: flex;
  align-items: center;
  gap: 6px;
}

.guia-sit-code {
  font-size: 9px;
  font-family: monospace;
  color: var(--oa-text-disabled);
  flex-shrink: 0;
}

.guia-sit-title {
  font-size: 13px;
  font-weight: 500;
  color: var(--oa-text-primary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.guia-sit-row:hover .guia-sit-title {
  color: var(--oa-gold-full);
}

.guia-sit-trigger {
  font-size: 11px;
  color: var(--oa-text-muted);
  font-style: italic;
  margin: 2px 0 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.guia-sit-diff-label {
  font-size: 9px;
  font-weight: 500;
  flex-shrink: 0;
}

.guia-sit-steps {
  font-size: 10px;
  color: var(--oa-text-disabled);
  flex-shrink: 0;
}
</style>
